/**
 * DurationTimelineChart.js — v2 (summary-dashboard-ready)
 *
 * A nuanced interactive timeline for survey durations.
 *
 * Upgrades over v1:
 *   1. Pan/zoom via d3-zoom — scroll to zoom into a date range, drag to pan
 *   2. Bi-color outlier dots — red for "too fast" (rushed/fraud signal),
 *      amber for "too slow" (disengagement signal), distinguishes risk type
 *   3. Rolling median trend line (7-point) on top of daily median — smooths
 *      short-term noise so you can see the real drift
 *   4. Refined header matching DimensionHeader aesthetic (breadcrumb-eyebrow,
 *      inline controls, quiet metadata) instead of a boxy control panel
 *   5. Keyboard: Esc resets zoom, R toggles range detection mode
 *   6. Graceful single-day rendering
 *   7. Reduced-motion friendly: no tween animations when preferred
 *   8. Fixed: the previous crosshair hover rects stole mouse events from outlier dots
 *
 * Backward compatible — accepts identical props as v1.
 */

import React, { useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { ChevronDown, RotateCcw, Zap, Clock } from 'lucide-react';

const DurationTimelineChart = ({
  data,
  byCollectorData = null,
  width: propWidth = 800,
  height = 420,
  isDarkMode = false,
  compact = false,             // NEW: when true, trims padding & legend for summary embed
}) => {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const zoomRef = useRef();

  const [containerWidth, setContainerWidth] = useState(0);
  const [outlierMode, setOutlierMode] = useState('user_defined');
  const [selectedCollector, setSelectedCollector] = useState('all');
  const [showDailyCounts, setShowDailyCounts] = useState(!compact);
  const [showRollingMedian, setShowRollingMedian] = useState(true);
  const [hoverDay, setHoverDay] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);

  /* ============================================================
   * Responsive width
   * ============================================================ */
  useEffect(() => {
    const update = () => {
      if (wrapperRef.current) {
        const w = wrapperRef.current.offsetWidth;
        if (w > 0) setContainerWidth(w);
      }
    };
    update();
    let ro;
    if (wrapperRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        if (entries[0]) {
          const w = entries[0].contentRect.width;
          if (w > 0) setContainerWidth(w);
        }
      });
      ro.observe(wrapperRef.current);
    }
    window.addEventListener('resize', update);
    const t = setTimeout(update, 100);
    return () => { ro && ro.disconnect(); window.removeEventListener('resize', update); clearTimeout(t); };
  }, []);

  // Use the container's actual measured width. propWidth is now only a
  // fallback for SSR/initial-render before ResizeObserver fires.
  const width = containerWidth;

  /* ============================================================
   * Data shaping
   * ============================================================ */
  const collectors = useMemo(
    () => (byCollectorData ? Object.keys(byCollectorData).sort() : []),
    [byCollectorData],
  );

  const filteredTimelineData = useMemo(() => {
    if (!data) return [];
    if (selectedCollector === 'all' || !byCollectorData) return data.timeline_data || [];
    return byCollectorData[selectedCollector]?.timeline_data || [];
  }, [data, byCollectorData, selectedCollector]);

  const hasUserDefinedRange = data?.duration_range
    && data.duration_range.min !== undefined
    && data.duration_range.max !== undefined;

  const unit = data?.unit || 'min';

  /* ---- Points + daily stats ------------------------------------ */
  const { points, dailyStats } = useMemo(() => {
    if (!filteredTimelineData?.length) return { points: [], dailyStats: [] };

    const pts = filteredTimelineData
      .filter(d => d.date && d.duration != null && !isNaN(d.duration))
      .map(d => ({
        date: new Date(d.date),
        duration: +d.duration,
        collector: d.collector || d.data_collector || 'Unknown',
      }))
      .sort((a, b) => a.date - b.date);

    const grouped = d3.rollup(
      pts,
      values => {
        const durations = values.map(v => v.duration).sort(d3.ascending);
        const q1 = d3.quantile(durations, 0.25);
        const median = d3.quantile(durations, 0.5);
        const q3 = d3.quantile(durations, 0.75);
        const iqr = q3 - q1;
        const whiskerLow  = Math.max(d3.min(durations), q1 - 1.5 * iqr);
        const whiskerHigh = Math.min(d3.max(durations), q3 + 1.5 * iqr);

        const statOutliers = durations.filter(d => d < whiskerLow || d > whiskerHigh);

        let belowMin = [], aboveMax = [];
        if (hasUserDefinedRange) {
          belowMin = durations.filter(d => d < data.duration_range.min);
          aboveMax = durations.filter(d => d > data.duration_range.max);
        }

        return {
          date: values[0].date,
          count: durations.length,
          q1, median, q3,
          whiskerLow, whiskerHigh,
          min: d3.min(durations),
          max: d3.max(durations),
          statOutliers,
          belowMin,
          aboveMax,
          allDurations: durations,
        };
      },
      d => d.date.toDateString(),
    );

    const arr = Array.from(grouped.values()).sort((a, b) => a.date - b.date);

    arr.forEach((day, i) => {
      const window = arr.slice(Math.max(0, i - 3), Math.min(arr.length, i + 4));
      const mids = window.map(w => w.median);
      day.rollingMedian = d3.mean(mids);
    });

    return { points: pts, dailyStats: arr };
  }, [filteredTimelineData, data, hasUserDefinedRange]);

  /* ============================================================
   * Summary stats
   * ============================================================ */
  const summary = useMemo(() => {
    if (!points.length) return null;
    const total = points.length;
    const durations = points.map(p => p.duration);

    const withinRange = hasUserDefinedRange
      ? durations.filter(d => d >= data.duration_range.min && d <= data.duration_range.max).length
      : total;

    const belowMin = hasUserDefinedRange
      ? durations.filter(d => d < data.duration_range.min).length : 0;
    const aboveMax = hasUserDefinedRange
      ? durations.filter(d => d > data.duration_range.max).length : 0;

    return {
      total,
      withinRange,
      withinPct: total > 0 ? (withinRange / total * 100).toFixed(1) : '0.0',
      belowMin, aboveMax,
      outOfRange: total - withinRange,
    };
  }, [points, data, hasUserDefinedRange]);

  /* ============================================================
   * Palette
   * ============================================================ */
  const palette = useMemo(() => ({
    bg:           'transparent',
    text:         isDarkMode ? '#E5E7EB' : '#1F2937',
    textMuted:    isDarkMode ? '#6B7280' : '#94A3B8',
    textDim:      isDarkMode ? '#9CA3AF' : '#64748B',
    grid:         isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.05)',
    axisLine:     isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
    median:       isDarkMode ? '#E5E7EB' : '#1E293B',
    rollingMed:   isDarkMode ? '#60A5FA' : '#2563EB',
    iqrFill:      isDarkMode ? 'rgba(156,163,175,0.22)' : 'rgba(100,116,139,0.18)',
    whiskerFill:  isDarkMode ? 'rgba(156,163,175,0.10)' : 'rgba(100,116,139,0.08)',
    okBand:       '#10B981',
    okBandFill:   isDarkMode ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.08)',
    tooFast:      '#DC2626',
    tooSlow:      '#D97706',
    statOutlier:  isDarkMode ? '#6B7280' : '#94A3B8',
    tooltipBg:    isDarkMode ? 'rgba(17,24,39,0.97)' : 'rgba(255,255,255,0.98)',
    tooltipBorder:isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
  }), [isDarkMode]);

  /* ============================================================
   * Chart geometry + scales
   * ============================================================ */
  const margin = compact
    ? { top: 16, right: 20, bottom: 40, left: 48 }
    : { top: 22, right: 20, bottom: 48, left: 52 };
  const innerWidth = Math.max(200, width - margin.left - margin.right);
  const innerHeight = Math.max(200, height - margin.top - margin.bottom);

  const [xDomain, setXDomain] = useState(null);

  useEffect(() => { setXDomain(null); }, [selectedCollector, data]);

  const fullXExtent = useMemo(
    () => (points.length ? d3.extent(points, p => p.date) : [new Date(), new Date()]),
    [points],
  );

  const xScale = useMemo(() => {
    const extent = xDomain || fullXExtent;
    let [start, end] = extent;
    if (start.getTime() === end.getTime()) {
      start = new Date(start.getTime() - 12 * 3600000);
      end   = new Date(end.getTime()   + 12 * 3600000);
    }
    return d3.scaleTime().domain([start, end]).range([0, innerWidth]);
  }, [xDomain, fullXExtent, innerWidth]);

  /* ---- Adaptive Y-scale ----------------------------------------
   * The old logic forced the domain to cover the full acceptable range
   * (20–120 min). When data clusters near one boundary (common in survey
   * work — enumerators rush or stall), this left 80% of the plot empty.
   *
   * New behavior:
   *   • Compute domain from the whisker range of data (p1–p99), with padding
   *   • Only *expand* to show the nearer acceptable-range line(s) if it's
   *     close to the data. If a reference line is more than 1× the data span
   *     away, we drop it and surface a small "↑ 120 min max" hint at the top
   *     of the chart instead.
   * -------------------------------------------------------------- */
  const yScale = useMemo(() => {
    if (!points.length) return d3.scaleLinear().domain([0, 100]).range([innerHeight, 0]);
    const durations = points.map(p => p.duration).sort(d3.ascending);
    // Use the 1st/99th percentile as the anchor, not absolute min/max.
    // Keeps extreme outliers from stretching the scale.
    const p01 = d3.quantile(durations, 0.01) ?? durations[0];
    const p99 = d3.quantile(durations, 0.99) ?? durations[durations.length - 1];
    const span = Math.max(1, p99 - p01);
    let lo = Math.max(0, p01 - span * 0.15);
    let hi = p99 + span * 0.25;

    if (hasUserDefinedRange) {
      // Include the min boundary only if it's within 1× data span of the data
      if (data.duration_range.min >= lo - span && data.duration_range.min <= hi + span) {
        lo = Math.min(lo, data.duration_range.min * 0.9);
      }
      // Same for max — only include if it's reasonably close
      if (data.duration_range.max <= hi + span) {
        hi = Math.max(hi, data.duration_range.max * 1.05);
      }
    }
    return d3.scaleLinear().domain([Math.max(0, lo), hi]).range([innerHeight, 0]).nice();
  }, [points, data, hasUserDefinedRange, innerHeight]);

  // Track which reference lines are actually on-screen so we know which
  // off-screen hints to render above/below the plot.
  const offScreenRefs = useMemo(() => {
    if (!hasUserDefinedRange || !yScale) return { aboveTop: null, belowBottom: null };
    const [yLo, yHi] = yScale.domain();
    return {
      aboveTop:    data.duration_range.max > yHi ? data.duration_range.max : null,
      belowBottom: data.duration_range.min < yLo ? data.duration_range.min : null,
    };
  }, [hasUserDefinedRange, yScale, data]);

  /* ============================================================
   * Pan & zoom
   * ============================================================ */
  const zoomBehaviorRef = useRef();

  useEffect(() => {
    if (!svgRef.current || !points.length) return;
    const svg = d3.select(svgRef.current);

    const zoom = d3.zoom()
      .scaleExtent([1, 20])
      .translateExtent([[0, 0], [innerWidth, innerHeight]])
      .extent([[0, 0], [innerWidth, innerHeight]])
      .on('zoom', (event) => {
        // Only react to user-initiated events (wheel/drag), not programmatic
        // transform calls made by the mini-map brush. This prevents feedback
        // loops when the brush and wheel-zoom both try to set xDomain.
        if (!event.sourceEvent) return;
        const t = event.transform;
        zoomRef.current = t;
        const newX = t.rescaleX(d3.scaleTime().domain(fullXExtent).range([0, innerWidth]));
        setXDomain(newX.domain());
      });

    zoomBehaviorRef.current = zoom;
    svg.call(zoom);
    svg.on('dblclick.zoom', null);

    return () => { svg.on('.zoom', null); };
  }, [points, fullXExtent, innerWidth, innerHeight]);

  // Keep d3-zoom transform in sync with xDomain when the brush updates it.
  // Without this, after using the mini-map, the wheel-zoom would reset to
  // whatever transform was last saved — jumping the view.
  useEffect(() => {
    if (!svgRef.current || !zoomBehaviorRef.current || !points.length) return;
    const fullScale = d3.scaleTime().domain(fullXExtent).range([0, innerWidth]);
    const current = xDomain || fullXExtent;
    const [s0, s1] = current.map(fullScale);
    const k = innerWidth / (s1 - s0);
    const tx = -s0 * k;
    const transform = d3.zoomIdentity.translate(tx, 0).scale(k);
    // Silently set the transform without triggering the 'zoom' event
    d3.select(svgRef.current).property('__zoom', transform);
    zoomRef.current = transform;
  }, [xDomain, fullXExtent, innerWidth, points]);

  /* ============================================================
   * Keyboard shortcuts
   * ============================================================ */
  const resetZoom = useCallback(() => {
    if (svgRef.current) {
      d3.select(svgRef.current).transition().duration(250)
        .call(d3.zoom().transform, d3.zoomIdentity);
    }
    setXDomain(null);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      if (e.key === 'Escape') { resetZoom(); }
      if (e.key === 'r' && hasUserDefinedRange) {
        setOutlierMode(m => m === 'user_defined' ? 'statistical' : 'user_defined');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasUserDefinedRange, resetZoom]);

  /* ============================================================
   * Path generators
   * ============================================================ */
  const whiskerArea = useMemo(() => d3.area()
    .x(d => xScale(d.date))
    .y0(d => yScale(d.whiskerLow))
    .y1(d => yScale(d.whiskerHigh))
    .curve(d3.curveMonotoneX), [xScale, yScale]);

  const iqrArea = useMemo(() => d3.area()
    .x(d => xScale(d.date))
    .y0(d => yScale(d.q1))
    .y1(d => yScale(d.q3))
    .curve(d3.curveMonotoneX), [xScale, yScale]);

  const medianLine = useMemo(() => d3.line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.median))
    .curve(d3.curveMonotoneX), [xScale, yScale]);

  const rollingMedianLine = useMemo(() => d3.line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.rollingMedian))
    .curve(d3.curveMonotoneX), [xScale, yScale]);

  /* ============================================================
   * Axis ticks
   * ============================================================ */
  const xTicks = useMemo(() => {
    const targetCount = Math.max(3, Math.min(12, Math.floor(innerWidth / 80)));
    return xScale.ticks(targetCount);
  }, [xScale, innerWidth]);

  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  /* ============================================================
   * Outlier tone classifier for statistical mode
   * ============================================================ */
  const statPercentiles = useMemo(() => {
    if (!points.length) return { q1: 0, q3: 0, iqr: 0 };
    const allDurations = points.map(p => p.duration).sort(d3.ascending);
    const q1 = d3.quantile(allDurations, 0.25);
    const q3 = d3.quantile(allDurations, 0.75);
    return { q1, q3, iqr: q3 - q1 };
  }, [points]);

  const classifyStatPoint = useCallback((dur) => {
    const { q1, q3, iqr } = statPercentiles;
    if (dur < q1 - 1.5 * iqr) return 'tooFast';
    if (dur > q3 + 1.5 * iqr) return 'tooSlow';
    return null;
  }, [statPercentiles]);

  /* ============================================================
   * Find day nearest to mouse x
   * ============================================================ */
  const findDay = useCallback((mouseX) => {
    if (!dailyStats.length) return null;
    const mouseDate = xScale.invert(mouseX);
    const bisect = d3.bisector(d => d.date).left;
    const idx = bisect(dailyStats, mouseDate);
    const before = dailyStats[idx - 1];
    const after  = dailyStats[idx];
    if (!before) return after;
    if (!after)  return before;
    return (mouseDate - before.date) < (after.date - mouseDate) ? before : after;
  }, [dailyStats, xScale]);

  const onMouseMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - margin.left;
    const y = e.clientY - rect.top  - margin.top;
    if (x < 0 || x > innerWidth || y < 0 || y > innerHeight) {
      setHoverDay(null); setTooltipPos(null); return;
    }
    const day = findDay(x);
    if (day) {
      setHoverDay(day);
      // Anchor to crosshair: the data point's pixel position in page coords,
      // not the mouse. Tooltip follows the data, not the pointer.
      const anchorX = rect.left + margin.left + xScale(day.date);
      const anchorY = rect.top  + margin.top  + yScale(day.median);
      setTooltipPos({ x: anchorX, y: anchorY });
    }
  };

  const onMouseLeave = () => { setHoverDay(null); setTooltipPos(null); };

  /* ============================================================
   * EMPTY STATE
   * ============================================================ */
  if (!points.length) {
    return (
      <div ref={wrapperRef} style={{
        width: '100%', height,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: palette.textMuted, fontSize: 13,
        border: `1px dashed ${palette.axisLine}`, borderRadius: 12,
      }}>
        No timeline data available
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={{
      width: '100%', maxWidth: '100%',
      fontFamily: "'Outfit', -apple-system, sans-serif",
    }}>
      {/* --------- HEADER --------- */}
      {!compact && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: 10, fontWeight: 500, letterSpacing: '0.16em',
            textTransform: 'lowercase', color: palette.textMuted,
            marginBottom: 3,
          }}>
            timeline analysis
          </div>
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: 20, flexWrap: 'wrap',
          }}>
            <div>
              <h3 style={{
                fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em',
                color: palette.text, margin: 0, marginBottom: 4,
              }}>
                Duration patterns over time
              </h3>
              <p style={{
                fontSize: 12, color: palette.textMuted, margin: 0,
                letterSpacing: '-0.005em',
              }}>
                Daily median (line), IQR (band), and out-of-range interviews (dots).
                Scroll to zoom · drag to pan · esc to reset.
              </p>
            </div>

            {summary && hasUserDefinedRange && (
              <div style={{ display: 'flex', gap: 14, fontSize: 12, color: palette.textDim,
                            flexWrap: 'wrap' }}>
                <StatChip label="total" value={summary.total} tone="neutral" palette={palette} />
                <StatChip label="within range" value={`${summary.withinRange} (${summary.withinPct}%)`}
                          tone="ok" palette={palette} />
                {summary.belowMin > 0 && (
                  <StatChip label="too fast" value={summary.belowMin} tone="fast" palette={palette} />
                )}
                {summary.aboveMax > 0 && (
                  <StatChip label="too slow" value={summary.aboveMax} tone="slow" palette={palette} />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --------- CONTROLS --------- */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        {collectors.length > 0 && (
          <InlineSelect
            label="Collector"
            value={selectedCollector}
            onChange={setSelectedCollector}
            options={[{ value: 'all', label: 'All Collectors' }, ...collectors.map(c => ({ value: c, label: c }))]}
            palette={palette}
          />
        )}

        {hasUserDefinedRange && (
          <InlineSelect
            label="Outliers"
            value={outlierMode}
            onChange={setOutlierMode}
            options={[
              { value: 'user_defined', label: 'User-defined range' },
              { value: 'statistical',  label: 'Statistical (IQR)' },
            ]}
            palette={palette}
          />
        )}

        <Toggle label="Rolling median" checked={showRollingMedian}
                onChange={setShowRollingMedian} palette={palette} />
        <Toggle label="Daily counts" checked={showDailyCounts}
                onChange={setShowDailyCounts} palette={palette} />

        {xDomain && (
          <button onClick={resetZoom} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '5px 10px', fontSize: 11, fontWeight: 500,
            background: 'transparent', border: `1px solid ${palette.axisLine}`,
            borderRadius: 6, cursor: 'pointer', color: palette.textDim,
            fontFamily: 'inherit',
          }}>
            <RotateCcw size={11} /> Reset zoom
          </button>
        )}
      </div>

      {/* --------- CHART --------- */}
      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 8,
                     minHeight: height }}>
        {width > 0 ? (
        <svg
          ref={svgRef}
          width={width}
          height={height}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          style={{ display: 'block', cursor: 'grab' }}
        >
          <defs>
            <clipPath id="duration-clip">
              <rect x={0} y={0} width={innerWidth} height={innerHeight} />
            </clipPath>
            <linearGradient id="duration-ok-band" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor={palette.okBand} stopOpacity="0.12" />
              <stop offset="100%" stopColor={palette.okBand} stopOpacity="0.03" />
            </linearGradient>
          </defs>

          <g transform={`translate(${margin.left},${margin.top})`}>
            {/* Grid */}
            {yTicks.map(t => (
              <line key={`gy-${t}`} x1={0} x2={innerWidth}
                    y1={yScale(t)} y2={yScale(t)}
                    stroke={palette.grid} strokeDasharray="2,3" />
            ))}

            <g clipPath="url(#duration-clip)">
              {hasUserDefinedRange && (
                <>
                  <rect
                    x={0}
                    y={yScale(data.duration_range.max)}
                    width={innerWidth}
                    height={yScale(data.duration_range.min) - yScale(data.duration_range.max)}
                    fill="url(#duration-ok-band)"
                  />
                  <line x1={0} x2={innerWidth}
                        y1={yScale(data.duration_range.min)}
                        y2={yScale(data.duration_range.min)}
                        stroke={palette.okBand} strokeWidth={1.2} strokeDasharray="6,4" opacity={0.6} />
                  <line x1={0} x2={innerWidth}
                        y1={yScale(data.duration_range.max)}
                        y2={yScale(data.duration_range.max)}
                        stroke={palette.okBand} strokeWidth={1.2} strokeDasharray="6,4" opacity={0.6} />
                </>
              )}

              <path d={whiskerArea(dailyStats) || ''}
                    fill={palette.whiskerFill} />
              <path d={iqrArea(dailyStats) || ''}
                    fill={palette.iqrFill} />
              <path d={medianLine(dailyStats) || ''}
                    fill="none" stroke={palette.median}
                    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />

              {showRollingMedian && dailyStats.length >= 5 && (
                <path d={rollingMedianLine(dailyStats) || ''}
                      fill="none" stroke={palette.rollingMed}
                      strokeWidth={1.6} strokeDasharray="3,3"
                      strokeLinecap="round" opacity={0.85} />
              )}

              {dailyStats.map((day) => {
                const allOutliers =
                  outlierMode === 'user_defined' && hasUserDefinedRange
                    ? [
                        ...day.belowMin.map(d => ({ duration: d, tone: 'tooFast' })),
                        ...day.aboveMax.map(d => ({ duration: d, tone: 'tooSlow' })),
                      ]
                    : day.statOutliers.map(d => ({
                        duration: d,
                        tone: classifyStatPoint(d),
                      })).filter(o => o.tone);
                const cx = xScale(day.date);
                return allOutliers.map((o, i) => (
                  <circle
                    key={`out-${day.date.getTime()}-${i}`}
                    cx={cx}
                    cy={yScale(o.duration)}
                    r={4}
                    fill={o.tone === 'tooFast' ? palette.tooFast : palette.tooSlow}
                    fillOpacity={0.85}
                    stroke={isDarkMode ? 'rgba(13,14,14,0.5)' : 'rgba(255,255,255,0.7)'}
                    strokeWidth={1}
                  />
                ));
              })}

              {hoverDay && (
                <g pointerEvents="none">
                  <line x1={xScale(hoverDay.date)} x2={xScale(hoverDay.date)}
                        y1={0} y2={innerHeight}
                        stroke={palette.textDim} strokeWidth={1}
                        strokeDasharray="2,3" opacity={0.6} />
                  <circle cx={xScale(hoverDay.date)} cy={yScale(hoverDay.median)}
                          r={6} fill={palette.median} fillOpacity={0.25} />
                  <circle cx={xScale(hoverDay.date)} cy={yScale(hoverDay.median)}
                          r={3.5} fill={palette.median} />
                </g>
              )}
            </g>

            <g transform={`translate(0,${innerHeight})`}>
              <line x1={0} x2={innerWidth} stroke={palette.axisLine} />
              {xTicks.map((t, i) => (
                <g key={`xt-${i}`} transform={`translate(${xScale(t)},0)`}>
                  <line y1={0} y2={4} stroke={palette.axisLine} />
                  <text y={16} textAnchor="middle"
                        fontSize={10.5} fill={palette.textMuted}
                        style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {d3.timeFormat('%b %d')(t)}
                  </text>
                </g>
              ))}
            </g>

            <g>
              {yTicks.map(t => (
                <text key={`yt-${t}`}
                      x={-8} y={yScale(t)} dy="0.32em"
                      textAnchor="end" fontSize={10.5} fill={palette.textMuted}
                      style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {t}
                </text>
              ))}
              <text transform={`rotate(-90)`}
                    x={-(innerHeight / 2)} y={-margin.left + 14}
                    textAnchor="middle" fontSize={10.5} fill={palette.textMuted}
                    style={{ letterSpacing: '0.02em', textTransform: 'lowercase' }}>
                duration ({unit})
              </text>
            </g>

            {/* Acceptable-range labels — render only when the line is
                within the visible Y-domain. Positioned top-right of their
                line, inside the plot area (no wasted right margin). */}
            {hasUserDefinedRange && !offScreenRefs.aboveTop && (
              <text x={innerWidth - 4}
                    y={yScale(data.duration_range.max) - 4}
                    textAnchor="end"
                    fontSize={10} fill={palette.okBand} fontWeight={600}
                    style={{ letterSpacing: '0.01em' }}>
                {data.duration_range.max} {unit} max
              </text>
            )}
            {hasUserDefinedRange && !offScreenRefs.belowBottom && (
              <text x={innerWidth - 4}
                    y={yScale(data.duration_range.min) - 4}
                    textAnchor="end"
                    fontSize={10} fill={palette.okBand} fontWeight={600}
                    style={{ letterSpacing: '0.01em' }}>
                {data.duration_range.min} {unit} min
              </text>
            )}

            {/* Off-screen hints — tiny pills at the top/bottom of the plot
                saying "↑ 120 min max (off-screen)" when data is clustered far
                from a boundary. Keeps the user informed without wasting space. */}
            {offScreenRefs.aboveTop && (
              <g transform={`translate(${innerWidth - 4}, -8)`}>
                <text textAnchor="end" fontSize={9.5}
                      fill={palette.okBand} fontWeight={600}
                      style={{ letterSpacing: '0.01em' }}>
                  ↑ {offScreenRefs.aboveTop} {unit} max
                </text>
              </g>
            )}
            {offScreenRefs.belowBottom && (
              <g transform={`translate(${innerWidth - 4}, ${innerHeight + 14})`}>
                <text textAnchor="end" fontSize={9.5}
                      fill={palette.okBand} fontWeight={600}
                      style={{ letterSpacing: '0.01em' }}>
                  ↓ {offScreenRefs.belowBottom} {unit} min
                </text>
              </g>
            )}

            {/* Daily counts — only when columns are wide enough to avoid
                label collision. Drops out gracefully on narrow/mobile views. */}
            {showDailyCounts && (innerWidth / dailyStats.length) >= 22 && dailyStats.map((d, i) => {
              const cx = xScale(d.date);
              if (cx < 0 || cx > innerWidth) return null;
              return (
                <text key={`cnt-${i}`}
                      x={cx} y={innerHeight + 32}
                      textAnchor="middle" fontSize={9} fill={palette.textMuted}>
                  {d.count}
                </text>
              );
            })}
          </g>
        </svg>
        ) : (
          // Skeleton placeholder until ResizeObserver fires (first paint only)
          <div style={{ width: '100%', height, opacity: 0.3 }} />
        )}

        {hoverDay && tooltipPos && width > 0 && (
          <DayTooltip
            day={hoverDay}
            x={tooltipPos.x}
            y={tooltipPos.y}
            unit={unit}
            palette={palette}
            hasUserDefinedRange={hasUserDefinedRange}
            range={data.duration_range}
          />
        )}
      </div>

      {/* ========== MINI-MAP BRUSH ========== */}
      {!compact && dailyStats.length >= 3 && width > 0 && (
        <MinimapBrush
          dailyStats={dailyStats}
          fullXExtent={fullXExtent}
          xDomain={xDomain}
          onDomainChange={setXDomain}
          margin={margin}
          width={width}
          palette={palette}
          hasUserDefinedRange={hasUserDefinedRange}
          durationRange={data.duration_range}
        />
      )}

      {!compact && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 14,
          paddingTop: 10, borderTop: `1px solid ${palette.axisLine}`,
          fontSize: 11, color: palette.textDim,
        }}>
          <LegendItem swatch={palette.median} label="Daily median" palette={palette} />
          {showRollingMedian && dailyStats.length >= 5 && (
            <LegendItem swatch={palette.rollingMed} label="7-day rolling median" dashed palette={palette} />
          )}
          <LegendItem swatch={palette.iqrFill} label="IQR (25–75th)" block palette={palette} />
          <LegendItem swatch={palette.whiskerFill} label="Whisker range" block palette={palette} />
          <LegendItem swatch={palette.tooFast}
                      label={`Too fast${hasUserDefinedRange ? ` (<${data.duration_range.min})` : ''}`}
                      dot palette={palette} />
          <LegendItem swatch={palette.tooSlow}
                      label={`Too slow${hasUserDefinedRange ? ` (>${data.duration_range.max})` : ''}`}
                      dot palette={palette} />
        </div>
      )}
    </div>
  );
};

/* ==================================================================
 *  SUB-COMPONENTS
 * ================================================================== */

const StatChip = ({ label, value, tone = 'neutral', palette }) => {
  const toneColor = {
    neutral: palette.text,
    ok:      palette.okBand,
    fast:    palette.tooFast,
    slow:    palette.tooSlow,
  }[tone] || palette.text;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 5,
      fontSize: 11.5, color: palette.textMuted,
    }}>
      <span style={{ textTransform: 'lowercase', letterSpacing: '0.02em' }}>{label}</span>
      <strong style={{ color: toneColor, fontSize: 13, fontWeight: 600,
                       fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </strong>
    </span>
  );
};

const InlineSelect = ({ label, value, onChange, options, palette }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 4px 4px 10px',
    background: 'transparent',
    border: `1px solid ${palette.axisLine}`,
    borderRadius: 6,
  }}>
    <span style={{ fontSize: 11, color: palette.textMuted,
                   textTransform: 'lowercase', letterSpacing: '0.02em' }}>
      {label}
    </span>
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          appearance: 'none', WebkitAppearance: 'none',
          padding: '3px 22px 3px 6px',
          fontSize: 11.5, fontWeight: 500,
          background: 'transparent', color: palette.text,
          border: 'none', borderRadius: 4,
          cursor: 'pointer', outline: 'none',
          fontFamily: 'inherit',
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={10} style={{
        position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
        color: palette.textMuted, pointerEvents: 'none',
      }} />
    </div>
  </div>
);

const Toggle = ({ label, checked, onChange, palette }) => (
  <label style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 11, color: palette.textMuted, cursor: 'pointer',
    padding: '4px 8px',
    border: `1px solid ${checked ? palette.axisLine : 'transparent'}`,
    borderRadius: 6,
    background: checked ? palette.iqrFill : 'transparent',
    transition: 'background 0.15s ease',
    userSelect: 'none',
  }}>
    <input type="checkbox" checked={checked}
           onChange={(e) => onChange(e.target.checked)}
           style={{
             cursor: 'pointer',
             width: 12, height: 12, margin: 0,
             accentColor: palette.rollingMed,
           }} />
    <span style={{ color: checked ? palette.text : palette.textMuted }}>{label}</span>
  </label>
);

const LegendItem = ({ swatch, label, block, dot, dashed, palette }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{
      display: 'inline-block',
      width: dot ? 8 : 18,
      height: dot ? 8 : (block ? 9 : 2),
      borderRadius: dot ? '50%' : 1,
      background: dashed
        ? `repeating-linear-gradient(to right, ${swatch} 0 3px, transparent 3px 5px)`
        : swatch,
    }} />
    <span>{label}</span>
  </span>
);

const DayTooltip = ({ day, x, y, unit, palette, hasUserDefinedRange, range }) => {
  const tooltipRef = useRef();

  // Initial guess: 14px right of anchor, vertically centered on anchor.
  // Flipped/clamped by useLayoutEffect AFTER measurement.
  const [pos, setPos] = useState({ left: x + 14, top: y - 60, ready: false });

  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const w = tooltipRef.current.offsetWidth;
    const h = tooltipRef.current.offsetHeight;
    const GAP = 14;
    const MARGIN = 8;

    // Prefer right of anchor, vertically centered; flip to left if it overflows.
    let left = x + GAP;
    let top  = y - h / 2;

    if (left + w > window.innerWidth - MARGIN) {
      left = x - w - GAP;
    }
    if (top < MARGIN)                              top = MARGIN;
    if (top + h > window.innerHeight - MARGIN)     top = window.innerHeight - h - MARGIN;
    if (left < MARGIN)                             left = MARGIN;

    setPos({ left, top, ready: true });
  }, [x, y]);

  const belowN = day.belowMin?.length || 0;
  const aboveN = day.aboveMax?.length || 0;
  const outN = belowN + aboveN;

  return (
    <div ref={tooltipRef} style={{
      position: 'fixed', left: pos.left, top: pos.top,
      zIndex: 10000, pointerEvents: 'none',
      padding: '9px 11px',
      background: palette.tooltipBg,
      border: `1px solid ${palette.tooltipBorder}`,
      borderRadius: 8, minWidth: 180,
      fontSize: 11.5, color: palette.text,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.06)',
      fontFamily: "'Outfit', -apple-system, sans-serif",
      // Avoid first-render flash while we measure
      opacity: pos.ready ? 1 : 0,
      transition: 'opacity 0.08s ease',
    }}>
      <div style={{
        fontSize: 10, fontWeight: 500,
        letterSpacing: '0.04em', textTransform: 'uppercase',
        color: palette.textMuted, marginBottom: 5,
      }}>
        {day.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
      </div>
      <Row label="interviews" value={day.count} palette={palette} bold />
      <Row label="median"     value={`${day.median.toFixed(1)} ${unit}`} palette={palette} />
      <Row label="IQR"        value={`${day.q1.toFixed(1)}–${day.q3.toFixed(1)}`} palette={palette} />
      <Row label="range"      value={`${day.min.toFixed(1)}–${day.max.toFixed(1)}`} palette={palette} />

      {hasUserDefinedRange && (belowN > 0 || aboveN > 0 || day.count > 0) && (
        <>
          <div style={{
            height: 1, background: palette.tooltipBorder,
            margin: '7px -11px',
          }} />
          {belowN > 0 && (
            <Row label={<span style={{ color: palette.tooFast }}>
              <Zap size={9} style={{ display: 'inline', marginRight: 3 }} />too fast
            </span>} value={`${belowN} (${((belowN/day.count)*100).toFixed(0)}%)`}
                 valueColor={palette.tooFast} palette={palette} />
          )}
          {aboveN > 0 && (
            <Row label={<span style={{ color: palette.tooSlow }}>
              <Clock size={9} style={{ display: 'inline', marginRight: 3 }} />too slow
            </span>} value={`${aboveN} (${((aboveN/day.count)*100).toFixed(0)}%)`}
                 valueColor={palette.tooSlow} palette={palette} />
          )}
          {outN === 0 && (
            <Row label="all within range" value="✓"
                 valueColor={palette.okBand} palette={palette} />
          )}
        </>
      )}
    </div>
  );
};

const Row = ({ label, value, bold, valueColor, palette }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '2px 0', fontSize: 11.5,
    fontVariantNumeric: 'tabular-nums',
  }}>
    <span style={{ color: palette.textMuted }}>{label}</span>
    <span style={{
      fontWeight: bold ? 700 : 500,
      color: valueColor || palette.text,
    }}>{value}</span>
  </div>
);

/* ==================================================================
 *  MINIMAP BRUSH — scroll-through view of the full timeline
 *  --------------------------------------------------------------
 *  Shows a thumbnail of ALL daily medians + out-of-range counts.
 *  A draggable selection window indicates what the main chart is
 *  currently showing. Click to recenter, drag to pan, drag edges
 *  to zoom. Double-click resets to full range.
 * ================================================================== */
const MINIMAP_HEIGHT = 48;
const HANDLE_VISUAL_WIDTH = 10;    // what the user sees
const HANDLE_HIT_WIDTH    = 32;    // invisible hit target around the handle
const HANDLE_VISUAL_HEIGHT = 22;

const MinimapBrush = ({
  dailyStats,
  fullXExtent,
  xDomain,
  onDomainChange,
  margin,
  width,
  palette,
  hasUserDefinedRange,
  durationRange,
}) => {
  const svgRef = useRef();
  const dragRef = useRef(null);  // { mode: 'move'|'resize-l'|'resize-r'|'click', startX, startDomain }

  const innerWidth = Math.max(200, width - margin.left - margin.right);
  const h = MINIMAP_HEIGHT;

  // Full-timeline scales for the minimap
  const xMini = useMemo(() => {
    const [s, e] = fullXExtent;
    return d3.scaleTime().domain([s, e]).range([0, innerWidth]);
  }, [fullXExtent, innerWidth]);

  const yMini = useMemo(() => {
    const allMedians = dailyStats.map(d => d.median).filter(v => v != null);
    const lo = d3.min(allMedians) * 0.9;
    const hi = d3.max(allMedians) * 1.1;
    return d3.scaleLinear().domain([Math.max(0, lo), hi]).range([h - 4, 4]);
  }, [dailyStats, h]);

  // Compact median line across full range
  const miniLine = useMemo(() => {
    return d3.line()
      .x(d => xMini(d.date))
      .y(d => yMini(d.median))
      .curve(d3.curveMonotoneX)(dailyStats) || '';
  }, [dailyStats, xMini, yMini]);

  // Compact out-of-range bar chart underneath (shows where the problems cluster)
  const maxOutlierCount = useMemo(() => {
    if (!hasUserDefinedRange) return 0;
    return d3.max(dailyStats, d => (d.belowMin?.length || 0) + (d.aboveMax?.length || 0)) || 0;
  }, [dailyStats, hasUserDefinedRange]);

  // Current window in pixel space
  const [winStart, winEnd] = useMemo(() => {
    const domain = xDomain || fullXExtent;
    return [xMini(domain[0]), xMini(domain[1])];
  }, [xDomain, fullXExtent, xMini]);

  const windowWidth = winEnd - winStart;
  const isFullRange = Math.abs(windowWidth - innerWidth) < 2;

  /* ============================================================
   * Interaction — drag to pan, handles to resize, click to recenter
   * ============================================================ */
  const pxToDate = (px) => xMini.invert(px);

  const applyWindow = (pxStart, pxEnd) => {
    // clamp to full range
    const clampedStart = Math.max(0, Math.min(pxStart, innerWidth - 10));
    const clampedEnd   = Math.max(clampedStart + 10, Math.min(pxEnd, innerWidth));
    const newDomain = [pxToDate(clampedStart), pxToDate(clampedEnd)];
    // If window now spans the full range (or very close), reset to null
    if (clampedStart <= 1 && clampedEnd >= innerWidth - 1) {
      onDomainChange(null);
    } else {
      onDomainChange(newDomain);
    }
  };

  const onPointerDown = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left - margin.left;

    // Identify what was hit via data-role attribute — works for both mouse and touch.
    // Walks up from the target until it finds a role or hits the SVG.
    let role = null;
    let node = e.target;
    while (node && node !== svgRef.current) {
      if (node.dataset && node.dataset.role) {
        role = node.dataset.role;
        break;
      }
      node = node.parentNode;
    }

    let mode;
    if (role === 'handle-left') {
      mode = 'resize-l';
    } else if (role === 'handle-right') {
      mode = 'resize-r';
    } else if (role === 'window') {
      mode = 'move';
    } else {
      // Track background — recenter on click/tap point, preserving width
      const halfW = windowWidth / 2;
      applyWindow(px - halfW, px + halfW);
      mode = 'move';
      dragRef.current = { mode, startX: px, startWinStart: px - halfW, startWinEnd: px + halfW };
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
      svgRef.current.style.cursor = 'grabbing';
      return;
    }

    dragRef.current = { mode, startX: px, startWinStart: winStart, startWinEnd: winEnd };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    svgRef.current.style.cursor = mode === 'move' ? 'grabbing' : 'ew-resize';
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) {
      // Hover cursor hint (desktop only; mobile ignores this)
      const role = e.target?.dataset?.role;
      let cursor = 'pointer';
      if (role === 'handle-left' || role === 'handle-right') cursor = 'ew-resize';
      else if (role === 'window') cursor = 'grab';
      if (svgRef.current) svgRef.current.style.cursor = cursor;
      return;
    }
    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left - margin.left;
    const { mode, startX, startWinStart, startWinEnd } = dragRef.current;
    const dx = px - startX;

    if (mode === 'move') {
      let s = startWinStart + dx;
      let end = startWinEnd + dx;
      if (s < 0)             { end -= s; s = 0; }
      if (end > innerWidth)  { s -= (end - innerWidth); end = innerWidth; }
      applyWindow(s, end);
    } else if (mode === 'resize-l') {
      applyWindow(startWinStart + dx, startWinEnd);
    } else if (mode === 'resize-r') {
      applyWindow(startWinStart, startWinEnd + dx);
    }
  };

  const onPointerUp = (e) => {
    dragRef.current = null;
    if (svgRef.current) svgRef.current.style.cursor = 'default';
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };

  const onDoubleClick = () => {
    onDomainChange(null);
  };

  /* ============================================================
   * RENDER
   * ============================================================ */
  return (
    <div style={{ marginTop: 8, position: 'relative', userSelect: 'none' }}>
      <svg
        ref={svgRef}
        width={width}
        height={h + 16}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        style={{ display: 'block', touchAction: 'pan-y' }}
      >
        <g transform={`translate(${margin.left}, 0)`}>
          {/* Track background */}
          <rect
            x={0} y={0}
            width={innerWidth} height={h}
            fill={palette.iqrFill}
            opacity={0.35}
            rx={4}
          />

          {/* Acceptable-range band (subtle) */}
          {hasUserDefinedRange && durationRange &&
            durationRange.min >= yMini.domain()[0] &&
            durationRange.max <= yMini.domain()[1] && (
            <rect
              x={0}
              y={yMini(durationRange.max)}
              width={innerWidth}
              height={yMini(durationRange.min) - yMini(durationRange.max)}
              fill={palette.okBand}
              opacity={0.06}
            />
          )}

          {/* Per-day outlier-count ticks (red bars at bottom) */}
          {hasUserDefinedRange && maxOutlierCount > 0 && dailyStats.map((d, i) => {
            const total = (d.belowMin?.length || 0) + (d.aboveMax?.length || 0);
            if (total === 0) return null;
            const barH = (total / maxOutlierCount) * 10;
            return (
              <rect
                key={`mini-out-${i}`}
                x={xMini(d.date) - 1}
                y={h - barH - 2}
                width={2}
                height={barH}
                fill={palette.tooFast}
                opacity={0.55}
                pointerEvents="none"
              />
            );
          })}

          {/* Median line */}
          <path
            d={miniLine}
            fill="none"
            stroke={palette.median}
            strokeWidth={1.25}
            opacity={0.7}
            pointerEvents="none"
          />

          {/* Unselected dimmers — grey out outside the current window */}
          <rect
            x={0} y={0}
            width={winStart} height={h}
            fill={palette.bg === 'transparent' ? 'rgba(15,23,42,0.08)' : palette.bg}
            opacity={0.55}
            pointerEvents="none"
          />
          <rect
            x={winEnd} y={0}
            width={innerWidth - winEnd} height={h}
            fill="rgba(15,23,42,0.08)"
            opacity={0.55}
            pointerEvents="none"
          />

          {/* Selection window — this is the "move" hit target */}
          <rect
            data-role="window"
            x={winStart} y={0}
            width={windowWidth} height={h}
            fill={isFullRange ? 'transparent' : 'rgba(37,99,235,0.05)'}
            stroke={palette.rollingMed}
            strokeWidth={1.5}
            rx={2}
            opacity={isFullRange ? 0.3 : 1}
            style={{ cursor: 'grab' }}
          />

          {/* Move affordance inside the window — subtle drag-dots glyph,
              visible on mobile where there's no hover state. Only shown when
              the window is wide enough (>60px) to avoid visual clutter. */}
          {!isFullRange && windowWidth > 60 && (
            <g pointerEvents="none" opacity={0.45}>
              {[-5, 0, 5].map((dx) => (
                <circle
                  key={`grip-${dx}`}
                  cx={winStart + windowWidth / 2 + dx}
                  cy={h / 2}
                  r={1.2}
                  fill={palette.rollingMed}
                />
              ))}
            </g>
          )}

          {/* ===== Left handle (touch-friendly) =====
              An invisible wide hit target with a visible narrow handle on top.
              Both carry data-role so pointer detection works regardless of which
              element the touch lands on. */}
          <g>
            {/* Invisible hit target — 32px wide, full minimap height */}
            <rect
              data-role="handle-left"
              x={winStart - HANDLE_HIT_WIDTH / 2}
              y={0}
              width={HANDLE_HIT_WIDTH}
              height={h}
              fill="transparent"
              style={{ cursor: 'ew-resize' }}
            />
            {/* Visible handle — narrow pill */}
            <rect
              data-role="handle-left"
              x={winStart - HANDLE_VISUAL_WIDTH / 2}
              y={h / 2 - HANDLE_VISUAL_HEIGHT / 2}
              width={HANDLE_VISUAL_WIDTH}
              height={HANDLE_VISUAL_HEIGHT}
              rx={3}
              fill={palette.rollingMed}
              opacity={isFullRange ? 0.5 : 1}
              style={{ cursor: 'ew-resize' }}
            />
            {/* Grip lines — give a visual hint that it's draggable */}
            <g pointerEvents="none">
              {[-2, 2].map((dx) => (
                <line
                  key={`lh-${dx}`}
                  x1={winStart + dx} x2={winStart + dx}
                  y1={h / 2 - 5} y2={h / 2 + 5}
                  stroke="rgba(255,255,255,0.7)"
                  strokeWidth={1}
                />
              ))}
            </g>
          </g>

          {/* ===== Right handle (touch-friendly) ===== */}
          <g>
            <rect
              data-role="handle-right"
              x={winEnd - HANDLE_HIT_WIDTH / 2}
              y={0}
              width={HANDLE_HIT_WIDTH}
              height={h}
              fill="transparent"
              style={{ cursor: 'ew-resize' }}
            />
            <rect
              data-role="handle-right"
              x={winEnd - HANDLE_VISUAL_WIDTH / 2}
              y={h / 2 - HANDLE_VISUAL_HEIGHT / 2}
              width={HANDLE_VISUAL_WIDTH}
              height={HANDLE_VISUAL_HEIGHT}
              rx={3}
              fill={palette.rollingMed}
              opacity={isFullRange ? 0.5 : 1}
              style={{ cursor: 'ew-resize' }}
            />
            <g pointerEvents="none">
              {[-2, 2].map((dx) => (
                <line
                  key={`rh-${dx}`}
                  x1={winEnd + dx} x2={winEnd + dx}
                  y1={h / 2 - 5} y2={h / 2 + 5}
                  stroke="rgba(255,255,255,0.7)"
                  strokeWidth={1}
                />
              ))}
            </g>
          </g>

          {/* Endpoint date labels */}
          <text
            x={0} y={h + 12}
            fontSize={9.5}
            fill={palette.textMuted}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {d3.timeFormat('%b %d')(fullXExtent[0])}
          </text>
          <text
            x={innerWidth} y={h + 12}
            textAnchor="end"
            fontSize={9.5}
            fill={palette.textMuted}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {d3.timeFormat('%b %d')(fullXExtent[1])}
          </text>

          {/* Range hint — small label showing current window dates, above handles when zoomed */}
          {!isFullRange && windowWidth > 60 && (
            <text
              x={winStart + windowWidth / 2} y={-4}
              textAnchor="middle"
              fontSize={9.5}
              fill={palette.rollingMed}
              fontWeight={500}
              style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}
              pointerEvents="none"
            >
              {d3.timeFormat('%b %d')(pxToDate(winStart))} – {d3.timeFormat('%b %d')(pxToDate(winEnd))}
            </text>
          )}
        </g>
      </svg>
    </div>
  );
};

export default DurationTimelineChart;